import { CalendarIcon, ChevronDownIcon, ChevronUpIcon, InfoOutlineIcon, TimeIcon } from '@chakra-ui/icons';
import { Box, Button, Checkbox, Collapse, Divider, Flex, Input, InputGroup, InputRightAddon, Radio, RadioGroup, Select, Stack, Text, Textarea, Tooltip, useColorModeValue, useToast, Link } from '@chakra-ui/react';
import AdScheduler from 'components/adspresso/AdScheduler';
import AttributeSetting from 'components/adspresso/AttributeSetting';
import DataSetPixelSelector from 'components/adspresso/DataSetPixelSelector';
import ExcludeLocations from 'components/adspresso/ExcludeLocations';
import LocationSelectorForShopping from 'components/adspresso/LocationSelectorForShopping';
import MinuteSecondSpinner from 'components/adspresso/MinuteSecondSpinner';
import currencySymbolMap from 'currency-symbol-map';
import { toZonedTime } from 'date-fns-tz';
import { useFormik } from 'formik';
import moment from 'moment';
import 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useCreateAdspressoAdsetsMutation, useGetAdspressoAdsetByIdQuery, useUpdateAdspressoAdsetMutation } from 'store/adspresso/adspressoAdsetapi';
import { useUpdatePublishAdsetMutation } from 'store/publishTracking/trackingApi';
import { removeEmptyValues } from 'utils/functions';
import { salesFormSchema, salesFormSchematrackEdit } from 'utils/schema';
import Audience from './Audience';
import BudgetScheduleSpecs from 'components/adspresso/BudgetScheduleSpecs';
import BidMultiplier from 'components/genericForms/BidMultuplier';

function pluralizeType(type) {
    const pluralMapping = {
        country: 'countries',
        city: 'cities',
        region: 'regions',
        zip: 'zips',
        neighborhood: 'neighborhoods',
        geo_market: 'geo_markets',
        electoral_district: 'electoral_districts',
        subcity: 'subcities',
        place: 'places',
        address: 'addresses'
    };
    return pluralMapping[type] || `${type}s`; // Default pluralize rule by appending 's'
}

const transformGeolocations = (locations) => {

    const geoLocations = {
        geo_locations: {}
    };

    // Iterate over each location and group them by their pluralized type
    locations.forEach(location => {
        const pluralizedType = pluralizeType(location.type);

        // Initialize the array for the type if not already initialized
        if (!geoLocations.geo_locations[pluralizedType]) {
            geoLocations.geo_locations[pluralizedType] = [];
        }

        // Push the location to the corresponding type array
        geoLocations.geo_locations[pluralizedType].push(location);
    });

    return geoLocations;
};


function AdvantagePlusShoppingSalesForm({ data, campaignData, adsetsListData, onEditModalClose, selectedAdset, onClose, isEditing, context, onSaveSuccess, selectedAdAccounts }) {

    const inputBackground = useColorModeValue('#F5F3F9', '#1e1530');
    const textBackground = useColorModeValue('#F5F3F9', '#1e1530');
    const backgroundColor = useColorModeValue('gray.50', 'gray.800');
    const textColor = useColorModeValue('gray.800', '#fff');
    const borderColor = useColorModeValue('gray.200', 'inherit');
    const hoverBorderColor = useColorModeValue('gray.300', 'gray.700');
    const inputTextColor = useColorModeValue('#421e66', '#b5b5cd')

    const toast = useToast();
    const { adsAccounts, objective, buying_type } = campaignData?.form ? campaignData?.form : campaignData;

    let currentAdsetData;
    if (context == "trackEdit") {
        currentAdsetData = adsetsListData?.form;
    } else {
        currentAdsetData = adsetsListData?.data?.filter((adset) => adset._id == selectedAdset)?.[0];
    }

    // console.log({ currentAdsetData });

    let firstAdAccountId;
    if (context == "trackEdit") {
        firstAdAccountId = campaignData?.adsAccount?.adsAccountId;
    } else {
        firstAdAccountId = adsAccounts[0]?.adsAccountId;
    }

    let firstBMID;
    if (context == "trackEdit") {
        firstBMID = campaignData?.adsAccount?.businessId;
    } else {
        firstBMID = adsAccounts[0]?.businessId;
    }

    const timeZone = selectedAdAccounts[0]?.timezone_name;

    let timeZoneAbbreviation = '';

    if (timeZone) {
        timeZoneAbbreviation = moment.tz(timeZone).format('z');
        if (!timeZoneAbbreviation || timeZoneAbbreviation.startsWith('+')) {
            timeZoneAbbreviation = 'UTC';
        }
    } else {
        timeZoneAbbreviation = '';
    }

    const [startTime, setStartTime] = useState(() => {
        if (currentAdsetData?.start_time && timeZone) {
            // Extract the time in HH:mm format from the start_time (Unix timestamp)
            const adjustedTime = moment.unix(currentAdsetData?.start_time).format("HH:mm");
            return adjustedTime;
        }

        // If no start_time is provided, set the default to the current time in the selected timeZone
        if (timeZone) {
            const currentTimeInTimeZone = moment.tz(timeZone).format("HH:mm");
            return currentTimeInTimeZone;
        }

        return "00:00"; // Fallback if no timezone is provided (or any other case)
    });

    const [startDate, setStartDate] = useState(() => {
        const date = currentAdsetData?.start_time
            ? new Date(currentAdsetData?.start_time * 1000)
            : new Date();

        // Convert to the specified timeZone (for current date)
        return currentAdsetData?.start_time ? date : toZonedTime(date, timeZone);
    });

    const [endTime, setEndTime] = useState(() => {
        if (currentAdsetData?.end_time && timeZone) {
            // Extract the time in HH:mm format from the end_time (Unix timestamp)
            const adjustedEndTime = moment.unix(currentAdsetData?.end_time).format("HH:mm");
            return adjustedEndTime;
        }

        // If no end_time is provided, set the default to the current time in the selected timeZone
        if (timeZone) {
            const currentEndTimeInTimeZone = moment.tz(timeZone).format("HH:mm");
            return currentEndTimeInTimeZone;
        }

        return "00:00"; // Fallback if no timezone 
    });

    const [endDate, setEndDate] = useState(() => {
        if (currentAdsetData?.end_time) {
            const date = new Date(currentAdsetData?.end_time * 1000);
            return date;
        }
        const defaultEndDate = new Date(startDate);
        defaultEndDate.setDate(defaultEndDate.getDate() + 30);
        return toZonedTime(defaultEndDate, timeZone);
    });

    const [errorDateMessage, setErrorDateMessage] = useState('');
    const [showEndDate, setShowEndDate] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [zipCodes, setzipCodes] = useState([]);
    const [isScheduled, setIsScheduled] = useState(false);
    const [adSchedule, setAdSchedule] = useState([]);
    const [isAttributeExpanded, setAttributeIsExpanded] = useState(false);
    const [isSchedulingExpanded, setIsSchedulingExpanded] = useState(false);
    const [updatedRegions, setUpdatedRegions] = useState([]);
    const [timezone, setTimezone] = useState(currentAdsetData?.adset_schedule && currentAdsetData.adset_schedule.length > 0 ? currentAdsetData?.adset_schedule[0].timezone_type : 'USER');
    const currencyCode = selectedAdAccounts[0]?.currency;
    const currencySymbol = currencySymbolMap(currencyCode)

    const { data: adsetData, error: adsetsError, isLoading: isLoadingadset, refetch } = useGetAdspressoAdsetByIdQuery({ adsetId: selectedAdset }, {
        skip: !selectedAdset,
    });

    const [createAdsetsData, { isLoading: isAdsetsCreating, error: createError }] = useCreateAdspressoAdsetsMutation();
    const [updateAdspressoAdset, { isLoading: isAdspressoAdsetUpdating }] = useUpdateAdspressoAdsetMutation({ adsetId: selectedAdset });
    const [updatePublishAdset] = useUpdatePublishAdsetMutation({});

    let dynamicValidationSchema = salesFormSchema;

    if (context !== "trackEdit") {
        const extendValidationSchema = (schema, adsAccountsLength) => {
            return schema.shape({
                promoted_object: schema.fields.promoted_object.test(
                    'match-length-with-adsAccounts',
                    'The number of promoted objects must match the number of ads accounts',
                    (value) => {
                        // Skip validation if adsAccounts.length is 1
                        if (adsAccountsLength === 1) {
                            return true;
                        }
                        return Array.isArray(value) && value.length === adsAccountsLength;
                    }
                ),
            });
        };

        dynamicValidationSchema = extendValidationSchema(salesFormSchema, adsAccounts.length);
    }

    const adSetFormik = useFormik({
        initialValues: {
            name: '',
            destination_type: 'WEBSITE',
            bid_strategy: 'LOWEST_COST_WITHOUT_CAP',
            optimization_goal: 'OFFSITE_CONVERSIONS',
            promoted_object: [],
            billing_event: 'IMPRESSIONS',
            bid_amount: '',
            roas_average_floor: '',
            budget: 'daily_budget',
            daily_budget: 800,
            lifetime_budget: 14000,
            is_budget_schedule_enabled: false,
            budget_schedule_specs: [
                {
                  time_start: moment().add(1, 'day').startOf('day').unix(),
                  time_end: moment().add(2, 'day').startOf('day').unix(),
                  budget_value_type: 'ABSOLUTE',
                  budget_value: 5,
                },
            ],
            bid_adjustments: '',
            geo_locations: [],
            cpa: '',
            attribution_spec: [
                { event_type: 'CLICK_THROUGH', window_days: 7 },
                { event_type: 'VIEW_THROUGH', window_days: 1 },
                { event_type: 'ENGAGED_VIDEO_VIEW', window_days: 1 },
            ],
            context: 'trackEdit',
        },
        enableReinitialize: false,
        context: { adsAccounts },
        validateOnChange: true,
        validateOnBlur: true,
        validationSchema: context == "trackEdit" ? salesFormSchematrackEdit : dynamicValidationSchema,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            const { budget, name, bid_strategy, destination_type, optimization_goal, attribution_spec, billing_event, daily_budget, bid_amount, roas_average_floor, lifetime_budget, promoted_object, geo_locations, cpa, bid_adjustments, is_budget_schedule_enabled, budget_schedule_specs } = values;

            // Filter attribution_spec based on optimization_goal and window_days
            const filteredAttributionSpec = attribution_spec.filter(spec => {
                // Exclude ENGAGED_VIDEO_VIEW if optimization_goal is 'VALUE'
                if (optimization_goal === 'VALUE' && spec.event_type === 'ENGAGED_VIDEO_VIEW') {
                    return false;
                }
                // Exclude items where window_days is null
                return spec.window_days !== null;
            });

            if (context !== 'trackEdit') {
                if (["OFFSITE_CONVERSIONS", "VALUE"].includes(values.optimization_goal) && values.promoted_object?.length !== adsAccounts?.length) {
                    setSubmitting(false);
                    return;
                }
            } else {
                if (Array.isArray(values.promoted_object) && values.promoted_object.length === 0 && ["OFFSITE_CONVERSIONS", "VALUE"].includes(values.optimization_goal)) {
                    setSubmitting(false);
                    return;
                }
            }

            let transformedPromotedObject;

            if (context !== 'trackEdit') {
                if ((optimization_goal === 'OFFSITE_CONVERSIONS') || (optimization_goal === 'VALUE')) {
                    transformedPromotedObject = promoted_object.map(obj => {
                        return obj;
                    });
                }
            } else {
                if (["OFFSITE_CONVERSIONS", "VALUE"].includes(optimization_goal)) {

                    const obj = Array.isArray(promoted_object) ? promoted_object[0] : promoted_object;

                    const { adsAccountId, ...rest } = obj;
                
                    transformedPromotedObject = rest[0];
                }
            }

            const bidDetails = bid_strategy === 'COST_CAP'
                ? { bid_amount: bid_amount * 100 }
                : bid_strategy === 'LOWEST_COST_WITH_MIN_ROAS'
                    ? { roas_average_floor: roas_average_floor * 100 }
                    : {};

            const geoLocations = geo_locations?.length ? transformGeolocations(geo_locations) : null;

            const updateStartTime = (startDate, time) => {
                return moment(startDate)
                    .set({
                        hours: parseInt(time.split(":")[0], 10),
                        minutes: parseInt(time.split(":")[1], 10),
                        seconds: 0,
                    })
                    .unix(); // Useing unix() to get Unix timestamp
            };

            const updateEndTime = (endDate, time) => {
                return moment(endDate)
                    .set({
                        hours: parseInt(time.split(":")[0], 10),
                        minutes: parseInt(time.split(":")[1], 10),
                        seconds: 0,
                    })
                    .unix();
            };

            const updatedStartTime = updateStartTime(startDate, startTime);
            const updatedEndTime = updateEndTime(endDate, endTime);

            const payload = {
                name,
                cpa: Number(cpa),
                destination_type, optimization_goal, billing_event,
                bid_strategy,
                promoted_object: transformedPromotedObject,
                campaign: campaignData._id,
                targeting: {
                    ...(currentAdsetData?.targeting?._id ? { _id: currentAdsetData?.targeting?._id } : {}),
                    ...geoLocations,
                },
                ...bidDetails,
                currency: currencyCode,
                ...(budget === 'daily_budget' ? { daily_budget: daily_budget * 100 } : {}),
                ...(budget === 'lifetime_budget' ? { lifetime_budget: lifetime_budget * 100 } : {}),
                start_time: updatedStartTime,
                ...(budget === 'lifetime_budget' ? { end_time: updatedEndTime } : {}),
                ...(budget === 'daily_budget' && showEndDate ? { end_time: updatedEndTime } : {}),
                ...(bid_adjustments && { bid_adjustments: bid_adjustments }), 
                adset_schedule: adSchedule,
                ...(is_budget_schedule_enabled && {
                    is_budget_schedule_enabled : is_budget_schedule_enabled,
                    budget_schedule_specs: budget_schedule_specs
                }),
                ...(adSchedule && adSchedule.length > 0 ? { pacing_type: ["day_parting"] } : {}),
                ...(['OFFSITE_CONVERSIONS', 'VALUE'].includes(optimization_goal) && {
                    attribution_spec: filteredAttributionSpec.length > 0 ? filteredAttributionSpec : undefined
                }),
            };


            const filteredPayload = removeEmptyValues(payload);
            // console.log({ filteredPayload });


            if (isEditing) {
                if (context === 'trackEdit') {

                    const updatePayload = {
                        payload: {
                            ...filteredPayload,
                            _id: currentAdsetData?._id,
                        },
                        trackingId: adsetsListData?._id,
                    };

                    updatePublishAdset(updatePayload)
                        .unwrap()
                        .then(response => {
                            toast({
                                position: "top-right",
                                title: "Ad set updated",
                                status: "success",
                                duration: 4000,
                                isClosable: true,
                            });
                            onEditModalClose();
                            resetForm();
                            adSetFormik.setFieldError('geo_locations', undefined);
                            onSaveSuccess();
                            onClose();
                        })
                        .catch(error => {
                            let errorMessage = "An error occurred while updating the adset.";
                            if (error && error.data && error.data.message) {
                                errorMessage = error.data.message.replace(/"/g, '');
                            } else if (error.message) {
                                errorMessage = error.message.replace(/"/g, '');
                            }
                            toast({
                                position: "top-right",
                                title: "Error updating Adset",
                                description: errorMessage,
                                status: "error",
                                duration: 4000,
                                isClosable: true,
                            });
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                } else {

                    const updatePayload = {
                        payload: filteredPayload,
                        adsetId: selectedAdset,
                    };

                    updateAdspressoAdset(updatePayload)
                        .unwrap()
                        .then(response => {
                            toast({
                                position: "top-right",
                                title: "Ad set updated",
                                status: "success",
                                duration: 4000,
                                isClosable: true,
                            });
                            //console.log('Adset updated successfully:', response);
                            onEditModalClose();
                            resetForm();
                            adSetFormik.setFieldError('geo_locations', undefined);
                            onSaveSuccess();
                            onClose();
                        })
                        .catch(error => {
                            let errorMessage = "An error occurred while updating the adset.";

                            if (error && error.data && error.data.message) {
                                errorMessage = error.data.message.replace(/"/g, '');
                            } else if (error.message) {
                                errorMessage = error.message.replace(/"/g, '');
                            }

                            toast({
                                position: "top-right",
                                title: "Error updating Adset",
                                description: errorMessage,
                                status: "error",
                                duration: 4000,
                                isClosable: true,
                            });
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                }
            } else {
                createAdsetsData({ payload: filteredPayload })
                    .unwrap()
                    .then(response => {
                        toast({
                            position: "top-right",
                            title: "New Ad set created",
                            status: "success",
                            duration: 4000,
                            isClosable: true,
                        });
                        //console.log('Adsets created successfully:', response);
                        resetForm();
                        adSetFormik.setFieldError('geo_locations', undefined);
                        onClose();
                    })
                    .catch(error => {
                        let errorMessage = "An error occurred while creating the adset.";

                        if (error && error.data && error.data.message) {
                            errorMessage = error.data.message.replace(/"/g, '');
                        } else if (error.message) {
                            errorMessage = error.message.replace(/"/g, '');
                        }

                        toast({
                            position: "top-right",
                            title: "Error creating Adset",
                            description: errorMessage,
                            status: "error",
                            duration: 4000,
                            isClosable: true,
                        });
                    })
                    .finally(() => {
                        setSubmitting(false);
                    });
            }
        },
    });

    const {
        name,
        destination_type,
        optimization_goal,
        promoted_object,
        bid_strategy,
        budget,
        targeting,
    } = data;

    const optimizationOptions = optimization_goal[adSetFormik.values.destination_type] || [];
    const promotedObjectFields = promoted_object?.[adSetFormik.values.optimization_goal] || {};


    useEffect(() => {
        if (currentAdsetData) {
            // Manually set form values
            adSetFormik.setValues({
                ...adSetFormik.values,
                name: currentAdsetData?.name || '',
                destination_type: currentAdsetData?.destination_type || 'WEBSITE',
                bid_strategy: currentAdsetData?.bid_strategy || 'LOWEST_COST_WITHOUT_CAP',
                optimization_goal: currentAdsetData?.optimization_goal || 'OFFSITE_CONVERSIONS',
                promoted_object: context === 'trackEdit' ? (!Array.isArray(currentAdsetData.promoted_object)
                    ? [currentAdsetData.promoted_object]
                    : [])
                    : (currentAdsetData?.promoted_object || []),
                billing_event: currentAdsetData?.billing_event || 'IMPRESSIONS',
                bid_amount: Number(currentAdsetData?.bid_amount) / 100 || '',
                roas_average_floor: Number(currentAdsetData?.roas_average_floor) / 100 || '',
                budget: currentAdsetData?.daily_budget ? 'daily_budget' : 'lifetime_budget',
                daily_budget: Number(currentAdsetData?.daily_budget / 100) || 800,
                lifetime_budget: Number(currentAdsetData?.lifetime_budget / 100) || 14000,
                is_budget_schedule_enabled: currentAdsetData?.is_budget_schedule_enabled ?? false,
                budget_schedule_specs: currentAdsetData?.budget_schedule_specs?.length
                ? currentAdsetData.budget_schedule_specs.map((spec) => ({
                    ...spec,
                    budget_value:
                    spec.budget_value_type === 'ABSOLUTE'
                        ? spec.budget_value / 100
                        : spec.budget_value,
                }))
                : [
                    {
                        time_start: moment().add(1, 'day').startOf('day').unix(),
                        time_end: moment().add(2, 'day').startOf('day').unix(),
                        budget_value_type: 'ABSOLUTE',
                        budget_value: 5,
                    },
                ],
                bid_adjustments: currentAdsetData?.bid_adjustments || '',                    
                geo_locations: currentAdsetData?.targeting?.geo_locations ? Object.values(currentAdsetData?.targeting?.geo_locations).flat() : [] || [],
                cpa: Number(currentAdsetData?.cpa) || '',
                attribution_spec: (currentAdsetData?.attribution_spec && currentAdsetData.attribution_spec.length > 0)
                    ? currentAdsetData.attribution_spec
                    : [
                        { event_type: 'CLICK_THROUGH', window_days: 7 },
                        { event_type: 'VIEW_THROUGH', window_days: 1 },
                        { event_type: 'ENGAGED_VIDEO_VIEW', window_days: 1 }
                    ],
                context: 'trackEdit',
            });
        }
    }, [currentAdsetData]);


    useEffect(() => {
        // Update bid strategy based on optimization_goal
        adSetFormik.setFieldValue('bid_strategy', currentAdsetData?.bid_strategy ? currentAdsetData?.bid_strategy : 'LOWEST_COST_WITHOUT_CAP');
    }, [currentAdsetData?.bid_strategy]);


    useEffect(() => {
        if (adSetFormik.values.optimization_goal !== currentAdsetData?.optimization_goal) {
            adSetFormik.setFieldValue('bid_amount', '');
            adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITHOUT_CAP');
        } else {
            adSetFormik.setFieldValue('bid_strategy', currentAdsetData?.bid_strategy);
        }
    }, [adSetFormik.values.optimization_goal]);


    useEffect(() => {
        if (adSetFormik.values.destination_type === "WEBSITE") {
            // Update the optimization goal only if it's not already set
            if (!currentAdsetData?.optimization_goal) {
                adSetFormik.setFieldValue('optimization_goal', 'OFFSITE_CONVERSIONS');
            }
        }
    }, [adSetFormik.values.destination_type]);


    useEffect(() => {
        if (adSetFormik.values.budget === 'lifetime_budget') {
            if (currentAdsetData?.end_time) {
                setEndDate(new Date(currentAdsetData?.end_time * 1000))
            } else {
                const newEndDate = new Date(startDate);
                newEndDate.setDate(newEndDate.getDate() + 30); // 30 days from startDate
                setEndDate(newEndDate);
            }
        }
    }, [adSetFormik.values.budget]);

    useEffect(() => {
        if (currentAdsetData?.daily_budget && currentAdsetData?.end_time) {
            setShowEndDate(true);
            setEndDate(new Date(currentAdsetData.end_time * 1000));
        } else {
            setShowEndDate(false);
        }
    }, [currentAdsetData]);

    useEffect(() => {
        if (endDate <= startDate) {
            setErrorDateMessage('End date must be greater than start date.');
        } else {
            setErrorDateMessage('');
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (currentAdsetData?.adset_schedule?.length > 0 && adSetFormik.values.budget === 'lifetime_budget') {
            setIsScheduled(true);
        } else {
            setIsScheduled(false);
        }
    }, [currentAdsetData, adSetFormik.values.budget]);

    const handleBudgetChange = (e) => {
        const selectedBudgetType = e.target.value;
        if (selectedBudgetType === 'daily_budget') {
            adSetFormik.setFieldValue('lifetime_budget', adSetFormik.values.lifetime_budget);
            adSetFormik.setFieldValue('daily_budget', adSetFormik.values.daily_budget);
        } else if (selectedBudgetType === 'lifetime_budget') {
            adSetFormik.setFieldValue('daily_budget', adSetFormik.values.daily_budget);
            adSetFormik.setFieldValue('lifetime_budget', adSetFormik.values.lifetime_budget);
        }
        adSetFormik.setFieldValue('budget', selectedBudgetType);
    };

    const handlePixelIdSelection = (id, adsAccountId) => {
        const updatedPromotedObjects = adSetFormik.values.promoted_object.filter(
            obj => obj.adsAccountId !== adsAccountId
        );

        updatedPromotedObjects.push({
            adsAccountId,
            pixel_id: id
        });

        adSetFormik.setFieldValue('promoted_object', updatedPromotedObjects);
    };

    const handleEventTypeSelection = (option, adsAccountId) => {

        let updatedPromotedObjects = adSetFormik.values.promoted_object;
        if (context !== 'trackEdit') {
            updatedPromotedObjects = adSetFormik.values.promoted_object.map(obj =>
                obj.adsAccountId === adsAccountId ? {
                    ...option,
                    adsAccountId,
                    pixel_id: obj?.pixel_id
                } : obj
            );

            // If the adsAccountId isn't found, add a new promoted object
            if (!adSetFormik.values.promoted_object.find(obj => obj.adsAccountId === adsAccountId)) {
                updatedPromotedObjects.push({
                    adsAccountId,
                    ...option
                });
            }
        } else {
            updatedPromotedObjects = [{
                ...option,
                pixel_id: adSetFormik.values.promoted_object[0]?.pixel_id
            }];
        }
        adSetFormik.setFieldValue('promoted_object', updatedPromotedObjects);
    };

    const handleLocationsChange = (locations) => {

        // Update the formik fields based on inclusion type
        adSetFormik.setFieldValue('geo_locations', locations);

        setTimeout(() => {
            adSetFormik.validateField('geo_locations'); // Trigger validation after state change
        }, 0);
    };

    useEffect(() => {
        if (adSchedule.length > 0 && adSetFormik.values.budget === 'lifetime_budget') {
            // Ensure that each schedule entry contains the correct timezone
            const updatedAdSchedule = adSchedule.map(scheduleEntry => ({
                ...scheduleEntry,
                timezone_type: timezone
            }));
            setAdSchedule(updatedAdSchedule);
        } else if (adSetFormik.values.budget === 'daily_budget') {
            setAdSchedule([]);
        }
    }, [timezone, adSetFormik.values.budget]);

    const handleAdScheduleChange = (newSchedule) => {
        const adScheduleData = newSchedule.map(scheduleEntry => ({
            ...scheduleEntry,
            timezone_type: timezone
        }));
        setAdSchedule(adScheduleData);
    };

    const handleBidChange = useCallback((data) => {
        adSetFormik.setFieldValue('bid_adjustments', data);
    }, [adSetFormik]);

    const handleSave = () => {
        // Set all fields to touched to trigger validation
        Object.keys(adSetFormik.values).forEach(field => {
            adSetFormik.setFieldTouched(field, true);
        });

        setIsSaving(true);

        const errors = adSetFormik.errors;
        const errorKeys = Object.keys(errors);

        // Check for validation errors
        if (errorKeys.length > 0) {
            const firstErrorKey = errorKeys[0];
            // Check if promoted_object is empty or has invalid entries
            const promotedObject = adSetFormik.values.promoted_object;
            const optimizationGoal = adSetFormik.values.optimization_goal.trim();
            if (
                (optimizationGoal === 'OFFSITE_CONVERSIONS' || optimizationGoal === 'VALUE') &&
                (
                    // Check if promotedObject is an array and is invalid
                    (Array.isArray(promotedObject) && (
                        promotedObject.length === 0 ||
                        promotedObject.some(obj => !obj || !obj.custom_event_type || !obj.pixel_id)
                    )) ||
                    // Check if promotedObject is an object and is invalid
                    (typeof promotedObject === 'object' && promotedObject !== null && (
                        Object.values(promotedObject).some(obj => !obj || !obj.custom_event_type || !obj.pixel_id)
                    ))
                )
            ) {
                // Scroll to the error container
                const errorContainer = document.querySelector('.error-container');
                if (errorContainer) {
                    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return;
            }

            // First, try to scroll to the element by name
            const errorElementByName = document.querySelector(`[name="${firstErrorKey}"]`);
            if (errorElementByName) {
                errorElementByName.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }

            // If not found, try to scroll to the element by error class
            const errorElementByClass = document.querySelector(`.error-class-${firstErrorKey}`);
            if (errorElementByClass) {
                errorElementByClass.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        if (
            (adSetFormik.values.budget === 'lifetime_budget' && (errorDateMessage || endDate <= startDate)) ||
            (adSetFormik.values.budget === 'daily_budget' && showEndDate && (errorDateMessage || endDate <= startDate))
        ) {
            return;
        }
        adSetFormik.handleSubmit();
    };

    const buildTargetingSpec = useMemo(() => {
        const geoLocations = adSetFormik.values.geo_locations || [];

        const geoLocationList = geoLocations?.length ? transformGeolocations(geoLocations) : null;

        return {
            ...geoLocationList,
        };
    }, [
        adSetFormik.values.geo_locations,
    ]);


    return (
        <Box borderRadius="md" width="100%">
            <Flex gap={'15px'} direction={{ base: "column", md: "row" }}>
                <Flex flex={0.6} direction="column" overflowY="auto" maxHeight="calc(100vh - 200px)">
                    <Box width="100%">
                        <form onSubmit={adSetFormik.handleSubmit}>
                            {/* Ad Set Name */}
                            <Flex direction="column" mb={4} bg={backgroundColor} p={4} borderRadius="md" gap={1}>
                                <Flex>
                                    <Text fontWeight="bold">{name.label}</Text>
                                    <Text display="inline-flex" fontSize="18px" color="red.400" mr={2}>*</Text>
                                </Flex>
                                <Input
                                    name="name"
                                    placeholder={`Enter ${name.label}`}

                                    value={adSetFormik.values.name}
                                    onChange={adSetFormik.handleChange}
                                    onBlur={adSetFormik.handleBlur}
                                    borderRadius="md"
                                    size={'sm'}
                                />
                                {adSetFormik.touched.name && adSetFormik.errors.name && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.name}</Text>
                                )}
                            </Flex>

                            {/* Conversion Location */}
                            <Flex direction="column" mb={4} bg={backgroundColor} p={4} borderRadius="md" gap={2}>
                                <Text fontWeight="bold">{destination_type.label1}</Text>
                                <Text fontWeight="bold">{destination_type.label2}</Text>
                                <RadioGroup
                                    name="destination_type"
                                    onChange={(value) => {
                                        adSetFormik.setFieldValue('destination_type', value);
                                        adSetFormik.setFieldValue('promoted_object', []);
                                    }}
                                    value={adSetFormik.values.destination_type}
                                    isDisabled={context === 'trackEdit'}  
                                >
                                    <Stack spacing={4} direction="column" mb={2}>
                                        {destination_type.options?.map((option) => {
                                            const allowedDestination = adsetData?.destination_type;
                                            const hasAds = adsetData?.ads?.length > 0;

                                            // Disable option if ads exist and option is NOT allowed destination type
                                            const isDisabledByAdset = hasAds && allowedDestination && option.value !== allowedDestination;

                                            return (
                                                <Flex
                                                    key={option.value}
                                                    align="center"
                                                    opacity={isDisabledByAdset ? 0.5 : 1}
                                                >
                                                    <Radio
                                                        colorScheme="brand"
                                                        value={option.value}
                                                        isDisabled={isDisabledByAdset}
                                                    />
                                                    <Stack ml={2} spacing={0}>
                                                        <Text>{option.label1}</Text>
                                                        <Text fontSize="sm">{option.label2}</Text>
                                                    </Stack>
                                                </Flex>
                                            );
                                        })}
                                    </Stack>
                                </RadioGroup>
                                {adSetFormik.touched.destination_type && adSetFormik.errors.destination_type && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.destination_type}</Text>
                                )}

                                {/* Performance Goal */}
                                {adSetFormik.values.destination_type && (
                                    <Flex direction="column" gap={2} mb={2}>
                                        <Flex alignItems={'center'}>
                                            <Text fontWeight="bold">{optimization_goal.label}</Text>
                                            <Tooltip
                                                borderRadius={5}
                                                label={
                                                    <Box fontSize="sm" p={'5px'}>
                                                        <Text>Choose what you want people to do when they see your ads. Your campaign objective should align with your overall business goals.</Text>
                                                    </Box>
                                                }
                                                fontSize="md"
                                            >
                                                <InfoOutlineIcon pointerEvents="all" ml={2} />
                                            </Tooltip>
                                        </Flex>

                                        {optimizationOptions.length === 1 ? (
                                            <Text border={'1px'} borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} bg={textBackground} color={inputTextColor} borderRadius="md" p={2} fontSize={'14px'} height={'32px'} display="flex" alignItems="center">
                                                {optimizationOptions[0].label}
                                            </Text>
                                        ) : (
                                            <Select
                                                name="optimization_goal"
                                                value={adSetFormik.values.optimization_goal}
                                                onChange={(e) => adSetFormik.setFieldValue('optimization_goal', e.target.value)}
                                                onBlur={adSetFormik.handleBlur}
                                                isDisabled={context === 'trackEdit'}
                                                borderRadius="md"
                                                size={'sm'}
                                            >
                                                {optimizationOptions.map((option, index) => (
                                                    <option key={`${option.value}-${index}`} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                        {adSetFormik.touched.optimization_goal && adSetFormik.errors.optimization_goal && (
                                            <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.optimization_goal}</Text>
                                        )}
                                    </Flex>
                                )}

                                <Flex direction="column" className='error-container'>
                                    {/* Promoted Object */}
                                    {(adSetFormik.values.optimization_goal === 'OFFSITE_CONVERSIONS' || adSetFormik.values.optimization_goal === 'VALUE') && (
                                        context === "trackEdit" ? (
                                            campaignData?.adsAccount && (
                                                <DataSetPixelSelector
                                                    key={`${campaignData?.adsAccount.adsAccountId}-0`}
                                                    adsAccount={campaignData?.adsAccount}
                                                    promotedObjectFields={promotedObjectFields}
                                                    promotedObjectData={currentAdsetData?.promoted_object}
                                                    onSelectedPixelId={(id) => handlePixelIdSelection(id, campaignData.adsAccount.adsAccountId)}
                                                    // onSelectedEventType={(type) => handleEventTypeSelection(type, campaignData.adsAccount.adsAccountId)}
                                                    onSelectedEventType={(option, adsAccountId) => handleEventTypeSelection(option, adsAccountId)}
                                                    onBlur={adSetFormik.handleBlur}
                                                    isSaving={isSaving}
                                                />
                                            )
                                        ) : (
                                            adsAccounts?.map((account, index) => (
                                                <Box key={`${account?.adsAccountId}-${index}`}>
                                                    <DataSetPixelSelector
                                                        key={`${account?.adsAccountId}-${index}`}
                                                        adsAccount={account}
                                                        promotedObjectFields={promotedObjectFields}
                                                        promotedObjectData={currentAdsetData?.promoted_object?.filter((field) => field?.adsAccountId === account?.adsAccountId)?.[0]}
                                                        onSelectedPixelId={(id) => handlePixelIdSelection(id, account.adsAccountId)}
                                                        // onSelectedEventType={(type) => handleEventTypeSelection(type, account.adsAccountId)}
                                                        onSelectedEventType={(option, adsAccountId) => handleEventTypeSelection(option, adsAccountId)}
                                                        adSetFormik={adSetFormik}
                                                        onBlur={adSetFormik.handleBlur}
                                                        isSaving={isSaving}
                                                    />
                                                    {
                                                        index != (adsAccounts?.length - 1) ? (
                                                            <Divider mb={'10px'} />
                                                        ) : null
                                                    }
                                                </Box>
                                            ))
                                        )
                                    )}

                                    {(adSetFormik.values.optimization_goal === "OFFSITE_CONVERSIONS" || "VALUE") && adSetFormik.touched.promoted_object && adSetFormik.errors.promoted_object && (
                                        <Text fontSize={'sm'} color="red.500" className="error-class-promoted_object">
                                            {/* {'Pixels & events are required'} */}
                                        </Text>
                                    )}

                                    {adSetFormik.touched.optimization_goal && adSetFormik.errors.promoted_object && (
                                        <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.promoted_object}</Text>
                                    )}
                                </Flex>

                                {/* Bid Amount Field */}
                                <Flex direction="column">
                                    {adSetFormik.values.optimization_goal &&
                                        bid_strategy?.option[1]?.optimization_goal.includes(adSetFormik.values.optimization_goal) && (
                                            <Flex direction="column">
                                                <Flex alignItems={'center'}>
                                                    <Text mb={2} fontWeight="bold">
                                                        Cost per result goal &nbsp;
                                                        <Text as="span" fontWeight="normal">Optional</Text>
                                                    </Text>

                                                    <Tooltip
                                                        borderRadius={5}
                                                        label={
                                                            <Box fontSize="sm" p={'5px'}>
                                                                <Text>
                                                                    Set a goal if you're aiming for a certain cost per result. To set your goal,
                                                                    use cost per result data from previous campaigns with the same optimisation event
                                                                    and attribution settings. If you don't set a cost per result goal, Meta will focus
                                                                    on spending your entire budget and getting the most results.<br></br>
                                                                    Cost per result goal is only available with some performance goal selections.
                                                                </Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                    >
                                                        <InfoOutlineIcon pointerEvents="all" ml={2} mb={2} />
                                                    </Tooltip>
                                                </Flex>

                                                <InputGroup>
                                                    <Input
                                                        name="bid_amount"
                                                        type="number"
                                                        placeholder={`${currencySymbol}X.XX`}
                                                        value={adSetFormik.values.bid_amount}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            adSetFormik.handleChange(e);
                                                            if (value) {
                                                                adSetFormik.setFieldValue('bid_strategy', 'COST_CAP');
                                                            } else {
                                                                adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITHOUT_CAP');
                                                            }
                                                        }}
                                                        onBlur={adSetFormik.handleBlur}
                                                        onWheel={(e) => e.target.blur()}
                                                        borderRadius={'md'}
                                                        size={'sm'}
                                                    />
                                                    <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                        {currencyCode}
                                                    </InputRightAddon>
                                                </InputGroup>

                                                {adSetFormik.touched.bid_amount && adSetFormik.errors.bid_amount && (
                                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.bid_amount}</Text>
                                                )}
                                            </Flex>
                                        )}
                                    {/* Conditionally render the bid strategy radio options if bid_amount has a value */}
                                    {(adSetFormik.values.bid_amount) && (adSetFormik.values.destination_type == "WEBSITE") && (
                                        <Flex direction="column" mt={2}>
                                            <Text fontWeight="bold">Bid strategy</Text>
                                            <RadioGroup
                                                name="bid_strategy"
                                                value={adSetFormik.values.bid_strategy}
                                                onChange={(value) => adSetFormik.setFieldValue('bid_strategy', value)}
                                            >
                                                <Stack direction="row" spacing={4}>
                                                    <Flex direction="row" align="center" spacing={2}>
                                                        <Radio colorScheme='brand' value="COST_CAP" />
                                                        <Flex direction="column" ml={2}>
                                                            <Text fontWeight="bold">Cost per result goal</Text>
                                                            <Text fontSize="sm" color="gray.500">Best for getting the most volume</Text>
                                                        </Flex>
                                                    </Flex>
                                                </Stack>
                                            </RadioGroup>
                                        </Flex>
                                    )}
                                </Flex>


                                {/* Conditional ROAS Amount Field */}
                                <Flex direction="column">
                                    {adSetFormik.values.optimization_goal &&
                                        bid_strategy?.option[2]?.optimization_goal.includes(adSetFormik.values.optimization_goal) && (
                                            <Flex direction="column">
                                                <Flex >
                                                    <Text mb={2} fontWeight="bold">
                                                        ROAS amount &nbsp;
                                                        <Text as="span" fontWeight="normal">Optional</Text>
                                                    </Text>

                                                </Flex>

                                                <InputGroup>
                                                    <Input
                                                        name="roas_average_floor"
                                                        type="number"
                                                        placeholder={`${currencySymbol}X.XX`}
                                                        value={adSetFormik.values.roas_average_floor}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            adSetFormik.handleChange(e);
                                                            if (value) {
                                                                adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITH_MIN_ROAS');
                                                            } else {
                                                                adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITHOUT_CAP');
                                                            }
                                                        }}
                                                        onBlur={adSetFormik.handleBlur}
                                                        borderRadius={'md'}
                                                        onWheel={(e) => e.target.blur()}
                                                        size={'sm'}
                                                    />
                                                    <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                        {currencyCode}
                                                    </InputRightAddon>
                                                </InputGroup>

                                                {adSetFormik.touched.roas_average_floor && adSetFormik.errors.roas_average_floor && (
                                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.roas_average_floor}</Text>
                                                )}
                                            </Flex>
                                        )}
                                    {/* Conditionally render the bid strategy radio options if roas_average_floor has a value */}
                                    {adSetFormik.values.roas_average_floor && (adSetFormik.values.optimization_goal == "VALUE") && (
                                        <Flex direction="column" mt={4}>
                                            <Text mb={2} fontWeight="bold">Bid strategy</Text>
                                            <RadioGroup
                                                name="bid_strategy"
                                                value={adSetFormik.values.bid_strategy}
                                                onChange={(value) => adSetFormik.setFieldValue('bid_strategy', value)}
                                            >
                                                <Stack direction="row" spacing={4}>
                                                    <Flex direction="row" align="center">
                                                        <Radio colorScheme="brand" value="LOWEST_COST_WITH_MIN_ROAS" />
                                                        <Flex direction="column" ml={2}>
                                                            <Text fontWeight="bold" fontSize="14px">Cost per ROAS</Text>
                                                        </Flex>
                                                    </Flex>
                                                </Stack>
                                            </RadioGroup>
                                        </Flex>
                                    )}
                                </Flex>

                                {/* Attribution setting Field */}
                                {(adSetFormik.values.optimization_goal === 'OFFSITE_CONVERSIONS' || adSetFormik.values.optimization_goal === 'VALUE') ? (
                                    <Box>
                                        <Divider my={4} />
                                        <Box>
                                            <Flex alignItems="center" justifyContent="space-between">
                                                <Flex flexDirection={'column'} gap={2}>
                                                    <Button onClick={() => setAttributeIsExpanded(!isAttributeExpanded)} variant="link" rightIcon={isAttributeExpanded ? <ChevronUpIcon color={textColor} /> : <ChevronDownIcon color={textColor} />}>
                                                        <Text fontSize={'14px'} color={'gray.800'}>{isAttributeExpanded ? 'Hide Options' : 'Show More Options'}</Text>
                                                    </Button>
                                                </Flex>
                                            </Flex>
                                        </Box>

                                        <Collapse in={isAttributeExpanded}>
                                            <Flex direction="column" gap={2} pt={4}>
                                                <Flex align={'center'} mt={'10px'}>
                                                    <Text fontWeight="bold">Attribution setting</Text>
                                                    <Box>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Box fontSize="12px" p={'5px'}>
                                                                    <Text textAlign={'justify'}>
                                                                        The attribution setting is a finite period of time during which conversions can be credited to your ads and used to inform campaign optimisation. For example, with 1-day engaged-view, our system will learn from conversions that happen within 1 day and show ads to people most likely to convert in a day. Multiple attribution settings can be selected for reporting. You can see more details when you use Compare attribution settings in the campaign table.
                                                                    </Text>
                                                                </Box>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                        </Tooltip>
                                                    </Box>
                                                </Flex>
                                                <AttributeSetting
                                                    setFieldValue={adSetFormik.setFieldValue}
                                                    attribution_spec={adSetFormik.values.attribution_spec}
                                                    optimization_goal={adSetFormik.values.optimization_goal}
                                                />
                                            </Flex>
                                        </Collapse>
                                    </Box>
                                ) : (null)}
                            </Flex>

                            {/* Bid Multiplier */}
                            <Flex bg={backgroundColor} p={4} flexDirection="column" borderRadius="md" gap={4} mt={4}>
                                <Flex
                                    gap={{ base: 2, md: 3 }}
                                    align="center"
                                    flexWrap="wrap"
                                    >
                                    <Text fontWeight="bold">
                                        Bid Multiplier
                                    </Text>

                                    <Tooltip
                                        borderRadius="md"
                                        maxW="300px"
                                        placement="right"
                                        label={
                                        <Box fontSize="12px" p={2}>
                                            <Text textAlign="justify">
                                                Bid multipliers enable advertisers to reduce the amount of audience
                                                segmentation required because it allows them to maintain a nuanced
                                                bidding strategy within a single ad set with one targeted audience. 
                                                For example, advertisers typically create ad sets so that they can 
                                                bid differently for various audience segments. For example, an 
                                                advertiser can create 3 ad sets corresponding to 3 different age 
                                                groups: 18–25, 26–35, 36–65. With bid multiplier, an advertiser can 
                                                create a single ad set, then use bid multipliers to bid differently 
                                                for each of those buckets.
                                            </Text>
                                        </Box>
                                        }
                                        fontSize="md"
                                        hasArrow
                                    >
                                        <InfoOutlineIcon cursor="pointer" />
                                    </Tooltip>

                                    <Link
                                        href="https://developers.facebook.com/docs/marketing-api/bidding-and-optimization/bid-multiplier/"
                                        isExternal
                                        fontSize={{ base: "xs", md: "sm" }}
                                        color={inputTextColor}
                                        whiteSpace="nowrap"
                                    >
                                        (Learn More About Facebook Bid Multipliers)
                                    </Link>
                                </Flex>
                                <BidMultiplier adsAccountId={firstAdAccountId} preFilledData={currentAdsetData?.bid_adjustments} onBidChange={handleBidChange} />
                            </Flex>

                            {/* Budget Scheduling */}
                            <Flex bg={backgroundColor} p={4} flexDirection="column" borderRadius="md" gap={4} mt={4}>
                                <Text fontWeight="bold">Budget & schedule</Text>
                                {/* Budget */}
                                <Flex alignItems={'center'}>
                                    <Text fontWeight="bold">{budget.label}</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p={'5px'}>
                                                <Text>
                                                    Your ad set budget is the daily or lifetime amount that you want to spend on this ad set.
                                                    A daily budget is the average that you'll spend every day. A lifetime budget is the maximum that you'll spend during the lifetime of your ad set.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                    </Tooltip>
                                </Flex>
                                <Flex>
                                    <Select
                                        name="budget"
                                        value={adSetFormik.values.budget}
                                        onChange={handleBudgetChange}
                                        onBlur={adSetFormik.handleBlur}

                                        borderRadius={"md"}
                                        borderRight={'none'}
                                        borderRightRadius="0"
                                        maxW={'200px'}
                                        size={'sm'}
                                    >
                                        {budget.options?.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Select>
                                    {adSetFormik.values.budget === 'daily_budget' && (
                                        <InputGroup>
                                            <Input
                                                name="daily_budget"
                                                placeholder={currencySymbol}
                                                onWheel={(e) => e.target.blur()}
                                                type="number"
                                                value={String(adSetFormik.values.daily_budget)}
                                                onChange={(e) => adSetFormik.setFieldValue('daily_budget', String(e.target.value))}
                                                onBlur={adSetFormik.handleBlur}
                                                borderRight="0"
                                                borderLeftRadius="none"
                                                size={'sm'}
                                            />
                                            <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                {currencyCode}
                                            </InputRightAddon>
                                        </InputGroup>
                                    )}
                                    {adSetFormik.values.budget === 'lifetime_budget' && (
                                        <InputGroup>
                                            <Input
                                                name="lifetime_budget"
                                                placeholder={currencySymbol}
                                                onWheel={(e) => e.target.blur()}
                                                type="number"
                                                value={adSetFormik.values.lifetime_budget}
                                                onChange={(e) => adSetFormik.setFieldValue('lifetime_budget', String(e.target.value))}
                                                onBlur={adSetFormik.handleBlur}
                                                borderRight="0"
                                                borderLeftRadius="none"
                                                size={'sm'}
                                            />
                                            <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                {currencyCode}
                                            </InputRightAddon>
                                        </InputGroup>
                                    )}
                                </Flex>
                                {adSetFormik.touched.budget && adSetFormik.errors.budget && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.budget}</Text>
                                )}
                                {adSetFormik.values.budget === 'daily_budget' && adSetFormik.touched.daily_budget && adSetFormik.errors.daily_budget && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.daily_budget}</Text>
                                )}
                                {adSetFormik.values.budget === 'lifetime_budget' && adSetFormik.touched.lifetime_budget && adSetFormik.errors.lifetime_budget && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.lifetime_budget}</Text>
                                )}

                                {/* Schedule */}
                                <Flex alignItems={'center'} mt={2}>
                                    <Text fontWeight="bold">Schedule</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p={'5px'}>
                                                <Text>
                                                    You can choose to run your ads continuously starting today or only during a specific date range.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                    </Tooltip>
                                </Flex>

                                <Flex direction="column" gap={2}>
                                    {/* Start Date */}
                                    <Text fontWeight={'bold'}>Start date</Text>
                                    <Flex alignItems="center" gap={6}>
                                        <Box flex="1" border="1px solid" borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} borderRadius="md" p={1} bg={inputBackground}>
                                            <Flex alignItems="center" gap={2}>
                                                <CalendarIcon color={inputTextColor} boxSize={3.5} ml={2} />
                                                <DatePicker
                                                    selected={startDate}
                                                    onChange={(date) => setStartDate(date)}
                                                    minDate={toZonedTime(new Date(), timeZone)}
                                                    dateFormat="dd/MM/yyyy"
                                                    timeFormat="HH:mm"
                                                    timeIntervals={15}
                                                    timeCaption="Time"
                                                    customInput={
                                                        <input
                                                            type="text"
                                                            className="datepicker-input"
                                                            placeholder={''}
                                                            style={{
                                                                fontSize: '13px',
                                                                padding: '4px',
                                                                border: 'none',
                                                                width: '190px',
                                                                height: '18px',
                                                                color: inputTextColor,
                                                                backgroundColor: 'inherit',
                                                            }}
                                                        />
                                                    }
                                                />
                                            </Flex>
                                        </Box>
                                        <Box flex="1" border="1px solid" borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} borderRadius="md" p={'4px'} bg={inputBackground}>
                                            <Flex alignItems="center" gap={2}>
                                                <TimeIcon color={inputTextColor} boxSize={3.5} ml={2}/>
                                                <MinuteSecondSpinner value={startTime} onChange={(newTime) => setStartTime(newTime)} />
                                                <Text fontSize="12px" color={inputTextColor}>{timeZoneAbbreviation}</Text>
                                            </Flex>
                                        </Box>
                                    </Flex>

                                    {/* End Date */}
                                    {(adSetFormik.values.budget === 'daily_budget' || adSetFormik.values.budget === 'lifetime_budget') && (
                                        <Flex direction="column" gap={2} mt={4}>
                                            {adSetFormik.values.budget === 'daily_budget' && (
                                                <Checkbox
                                                    colorScheme='brand'
                                                    isChecked={showEndDate}
                                                    onChange={() => setShowEndDate(!showEndDate)}
                                                >
                                                    Set an end date
                                                </Checkbox>
                                            )}
                                            {(adSetFormik.values.budget === 'lifetime_budget' || showEndDate) && (
                                                <>
                                                    <Text fontWeight={'bold'}>End date</Text>
                                                    <Flex alignItems="center" gap={6}>
                                                        <Box flex="1" border="1px solid" borderColor={borderColor}  _hover={{ borderColor: hoverBorderColor }} borderRadius="md" p={1} bg={inputBackground}>
                                                            <Flex alignItems="center" gap={2}>
                                                                <CalendarIcon color={inputTextColor} boxSize={3.5} ml={2} />
                                                                <DatePicker
                                                                    selected={endDate}
                                                                    onChange={(date) => setEndDate(date)}
                                                                    minDate={toZonedTime(new Date(), timeZone)}
                                                                    dateFormat="dd/MM/yyyy"
                                                                    timeFormat="HH:mm"
                                                                    timeIntervals={15}
                                                                    timeCaption="Time"
                                                                    customInput={
                                                                        <input
                                                                            type="text"
                                                                            className="datepicker-input"
                                                                            placeholder={''}
                                                                            style={{
                                                                                color: inputTextColor,
                                                                                fontSize: '13px',
                                                                                padding: '4px',
                                                                                border: 'none',
                                                                                width: '190px',
                                                                                height: '18px',
                                                                                backgroundColor: 'inherit',
                                                                            }}
                                                                        />
                                                                    }
                                                                />
                                                            </Flex>
                                                        </Box>
                                                        <Box flex="1" border="1px solid" borderColor={borderColor}  _hover={{ borderColor: hoverBorderColor }} borderRadius="md" p={'4px'} bg={inputBackground}>
                                                            <Flex alignItems="center" gap={2}>
                                                                <TimeIcon color={inputTextColor} boxSize={3.5} ml={2}/>
                                                                <MinuteSecondSpinner value={endTime} onChange={(newTime) => setEndTime(newTime)} />
                                                                <Text fontSize="12px" color={inputTextColor}>{timeZoneAbbreviation}</Text>
                                                            </Flex>
                                                        </Box>
                                                    </Flex>
                                                    <Flex>
                                                        {errorDateMessage && <Text fontSize={'12px'} color="red.500">{errorDateMessage}</Text>}
                                                    </Flex>
                                                </>
                                            )}
                                        </Flex>
                                    )}
                                </Flex>

                                <Button justifyContent={'flex-start'} onClick={() => setIsSchedulingExpanded(!isSchedulingExpanded)} variant="link" rightIcon={isSchedulingExpanded ? <ChevronUpIcon color={textColor} /> : <ChevronDownIcon color={textColor} />}>
                                    <Text fontSize={'14px'} color={textColor}>{isSchedulingExpanded ? 'Hide Options' : 'Show More Options'}</Text>
                                </Button>

                                 {/* Collapsible content */}
                                <Collapse in={isSchedulingExpanded}>

                                    <Flex direction={'column'} gap={4}>
                                        <Flex direction={'column'} gap={2}>
                                            <Flex direction={'column'}>
                                                <Text fontWeight="bold">Budget scheduling</Text>
                                                <Text fontSize={'12px'}>Increase your budget during specific days or times.</Text>
                                            </Flex>
                                            <Flex>
                                                <Tooltip
                                                    borderRadius={5}
                                                    p={2}
                                                    ml={20}
                                                    placement='top'
                                                    fontSize={'sm'}
                                                    label="Your high-demand periods are turned off and will not run until you enable budget scheduling."
                                                >
                                                    <span>
                                                        <Checkbox
                                                            colorScheme={"brand"}
                                                            name="is_budget_schedule_enabled"
                                                            size={'md'}
                                                            isChecked={adSetFormik.values.is_budget_schedule_enabled}
                                                            onChange={adSetFormik.handleChange}
                                                            isDisabled={adSetFormik.values.budget !== 'daily_budget'}
                                                        >
                                                            Schedule budget increases
                                                        </Checkbox>
                                                    </span>
                                                </Tooltip>
                                            </Flex>
                                            {adSetFormik?.values?.is_budget_schedule_enabled && adSetFormik?.values?.budget === 'daily_budget' ? 
                                                <BudgetScheduleSpecs
                                                    specs={adSetFormik?.values?.budget_schedule_specs}
                                                    setFieldValue={adSetFormik?.setFieldValue}
                                                    currency={selectedAdAccounts[0]?.currency}
                                                />
                                            :null}
                                        </Flex>

                                        <Flex direction={'column'} gap={2}>
                                            <Flex alignItems={'center'}>
                                                <Text fontWeight="bold">Ad scheduling</Text>
                                                <Tooltip
                                                    borderRadius={5}
                                                    label={
                                                        <Box fontSize="12px" p={'5px'}>
                                                            <Text>
                                                                You can schedule your ads to deliver on specific hours and days of the week based on the viewer's or your ad account's time zone.
                                                            </Text>
                                                        </Box>
                                                    }
                                                    fontSize="md"
                                                >
                                                    <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                </Tooltip>
                                            </Flex>
                                            <Flex>
                                                <Tooltip
                                                    borderRadius={5}
                                                    p={2}
                                                    ml={20}
                                                    label="Ad scheduling is only available when you use lifetime budget."
                                                    isDisabled={adSetFormik.values.budget === 'lifetime_budget'}
                                                >
                                                    <span>
                                                        <Checkbox
                                                            colorScheme='brand'
                                                            isChecked={isScheduled}
                                                            onChange={(e) => setIsScheduled(e.target.checked)}
                                                            isDisabled={adSetFormik.values.budget !== 'lifetime_budget'}
                                                        >
                                                            Run ads on a schedule
                                                        </Checkbox>
                                                    </span>
                                                </Tooltip>
                                            </Flex>
                                        </Flex>
                                    </Flex>

                                    {isScheduled && adSetFormik.values.budget === 'lifetime_budget' && (
                                        <Flex direction={'column'} gap={2} mt={2}>
                                            <Flex direction={'column'} gap={2}>
                                                <Select
                                                    value={timezone}
                                                    onChange={(e) => setTimezone(e.target.value)}
                                                    size={'sm'}
                                                    borderRadius="md"
                                                >
                                                    <option value="USER">Use viewer's time zone</option>
                                                    <option value="ADVERTISER">Use this ad account's time zone</option>
                                                </Select>
                                                {timezone == "USER" && <Text fontSize={'12px'}>We'll schedule your ads based on the time zone of the person seeing it.</Text>}
                                                {timezone == "ADVERTISER" && <Text fontSize={'12px'}>We'll schedule your ads based on ad account's time zone</Text>}
                                            </Flex>
                                            <AdScheduler onAdScheduleChange={handleAdScheduleChange} adsetScheduleData={currentAdsetData?.adset_schedule} />
                                        </Flex>
                                    )}
                                </Collapse>
                            </Flex>

                            {/* Audience controls  */}
                            <Flex bg={backgroundColor} p={4} flexDirection="column" borderRadius="md" gap={4} my={4} overflow="visible" zIndex={1000} >
                                <Flex alignItems={'center'}>
                                    <Text fontWeight="bold">Audience controls</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p={'5px'}>
                                                <Text>
                                                    <strong>Setting audience controls </strong><br></br>
                                                    With Advantage+ audience, our system searches within these criteria for a more specific audience. To give our system flexibility to get results, we recommend setting these controls to create a broad audience.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                    </Tooltip>
                                </Flex>
                                {/* Targeting */}
                                <Flex flexDirection="column">
                                    <Flex alignItems={'center'}>
                                        <Text fontWeight="bold">{targeting.geo_locations.label}</Text>
                                        <Text display="inline-flex" fontSize="18px" color="red.400">*</Text>
                                        <Tooltip
                                            borderRadius={5}
                                            label={
                                                <Box fontSize="sm" p={'5px'}>
                                                    <Text>
                                                        Choosing a broad area to show your ads within can improve results. For example, by adding a country instead of several cities.
                                                    </Text>
                                                </Box>
                                            }
                                            fontSize="md"
                                        >
                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                        </Tooltip>
                                    </Flex>
                                    <Text fontSize={'14px'}>Reach people living in or recently in this location.</Text>
                                    <LocationSelectorForShopping
                                        preFilledIncludedLocations={currentAdsetData?.targeting?.geo_locations}
                                        onLocationsChange={handleLocationsChange}
                                        onBlur={adSetFormik.handleBlur}
                                        updatedRegions={updatedRegions}
                                        adsAccountData={firstAdAccountId}
                                    />
                                    {adSetFormik.touched.geo_locations && adSetFormik.errors.geo_locations && (
                                        <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.geo_locations}</Text>
                                    )}
                                </Flex>
                                <Flex>
                                    <ExcludeLocations adsAccountId={firstAdAccountId} onRegionsUpdated={(regions) => setUpdatedRegions(regions)} />
                                </Flex>
                            </Flex>


                            <Flex direction="column" bg={backgroundColor} p={4} borderRadius="md" gap={1}>
                                <Flex>
                                    <Text fontWeight="bold">Breakeven CPA</Text>
                                </Flex>
                                <Input
                                    name="cpa"
                                    type="number"
                                    placeholder={`Enter the cpa`}
                                    bg="#fff"
                                    value={adSetFormik.values.cpa}
                                    onChange={adSetFormik.handleChange}
                                    onBlur={adSetFormik.handleBlur}
                                    borderRadius="md"
                                    size={'sm'}
                                    onWheel={(e) => e.target.blur()}
                                />
                                {/* {adSetFormik.touched.cpa && adSetFormik.errors.cpa && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.cpa}</Text>
                                )} */}
                            </Flex>

                            {/* Submit Button */}
                            <Flex justify="flex-start" my={'20px'} gap={2}>
                                <Button 
                                    colorScheme={'gray'}
                                    variant={'solid'}
                                    size={'sm'}
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    colorScheme="brand"
                                    isLoading={adSetFormik.isSubmitting}
                                    // type="submit"
                                    onClick={handleSave}
                                    size={'sm'}>Save</Button>
                            </Flex>
                        </form>
                    </Box>
                </Flex>
                <Flex flex={0.4} direction="column" position="sticky" top="0" maxHeight="75vh" overflowY="auto">
                    <Audience adsAccount={firstAdAccountId} targeting_spec={buildTargetingSpec} optimizationGoal={adSetFormik.values?.optimization_goal} />
                </Flex>
            </Flex>
        </Box>
    );
}

export default AdvantagePlusShoppingSalesForm;